import { Button, Divider, Form, Input, Modal, Tag, message, notification } from "antd";
import { FaEdit, FaSearch, FaTrash } from "react-icons/fa";
import moment from "moment";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { urlImgMembros } from "../../services/urls";
import { EmitError } from "../../utils/EmitError";
import { ThemeContext } from "../../contexts/ThemeContext";
import useAuth from "../../hooks/useAuth";
import MembrosService from "../../services/http/services/MembrosService";

export default function useMembros() {

    const refController = useRef(new AbortController());
    const { userCan } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const refFormMembro = useRef<any>(null);
    const [formMembro] = Form.useForm();
    const [isModalMembroOpen, setIsModalMembroOpen] = useState(false);
    const [isEditarMembro, setIsEditarMembro] = useState(false);
    const [fotoMembro, setFotoMembro] = useState("");
    const [membros, setMembros] = useState<any>([]);
    const [imagemModal, setImagemModal] = useState<any>(null);
    const [isModalImagemOpen, setIsModalImagemOpen] = useState(false);
    const [ordensMembros, setOrdensMembros] = useState<any>([]);
    const [membroEditar, setMembroEditar] = useState<any | null>(null);
    const { theme } = useContext(ThemeContext);
    const refContagemDoFiltroMembros = useRef<Array<any>>([]);
    const [dataEntity, setdataEntity] = useState(
        JSON.parse(localStorage.getItem('@GovfacilGestor:selectedEntity')!) || null
    );

    useEffect(() => {
        return () => refController.current.abort();
    }, []);

    const onClickBotaoSalvarMembro = useCallback((): void => {
        //Pega os campos do formulário
        refFormMembro.current
            .validateFields()
            .then((values: any) => {

                values.entityId = dataEntity.entity.id;
                salvarMembro(values);
            })
            .catch((errorInfo: any) => {
                notification.error({
                    message: "Erro",
                    description: "Preencha os campos obrigatorios!",
                });
            });
    }, []);

    const handleMembroCancel = () => {
        setIsModalMembroOpen(false);
    };

    const showModalMembro = async () => {
        let membroOrdens = membros.map((membro: any) => {
            //retorna ordem e id
            return { order: membro.order, id: membro.id };
        });

        setOrdensMembros(membroOrdens);
        formMembro.setFieldsValue({
            membroOrdens: membroOrdens,
        });

        setIsModalMembroOpen(true);
    };

    const beforeUploadMembro = useCallback(async (file: any): Promise<any> => {
        const isJpgOrPng =
            file.type === "image/png" ||
            file.type === "image/jpeg" ||
            file.type === "image/jpg";
        if (!isJpgOrPng) {
            message.error("Somente arquivos [png, jpg, jpeg] são suportados!");
            return;
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error("Imagem não pode ser maior que 2MB!");
            return;
        }

        //Pega proporção da imagem
        const proporcaoImagem = await getProporcaoImagem(file);

        if (proporcaoImagem.width !== proporcaoImagem.height) {
            message.error("Imagem deve ser quadrada!");
            return;
        }

        formMembro.setFieldsValue({
            fotoMembro: file,
        });

        setFotoMembro(file);
    }, []);

    const getProporcaoImagem = useCallback((file: any): Promise<any> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const { width, height } = img;
                resolve({ width, height });
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        });
    }, []);

    const getColumnSearchProps = useCallback(
        (dataIndex: any) => ({
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={(node) => { }}
                        placeholder={`Buscar ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => {
                            setSelectedKeys(
                                e.target.value ? [e.target.value] : []
                            );
                        }}
                        onPressEnter={() => {
                            confirm();
                        }}
                        style={{
                            width: 188,
                            marginBottom: 8,
                            display: "block",
                        }}
                    />
                    <Button
                        type="primary"
                        onClick={() => {
                            confirm();
                        }}
                        icon={<FaSearch />}
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={clearFilters}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Limpar
                    </Button>
                </div>
            ),
            filterIcon: (filtered: any) => (
                <FaSearch
                    style={{ color: filtered ? "#1890ff" : undefined }}
                />
            ),
            onFilter: (value: any, record: any) =>
                record[dataIndex]
                    ? record[dataIndex]
                        .toString()
                        .toLowerCase()
                        .includes(value.toLowerCase())
                    : "",
        }),
        []
    );

    const excluirMembro = useCallback((id: number) => {
        try {
            //Confirmação de exclusão
            Modal.confirm({
                title: "Excluir membro",
                content:
                    "Deseja realmente excluir o membro? (Essa ação não pode ser desfeita)",
                okText: "Sim",
                cancelText: "Não",
                onOk() {
                    setIsLoading(true);
                    MembrosService.deleteGestorMembro(
                        id,
                        refController.current.signal
                    ).then((result) => {
                        notification.success({
                            message: "Sucesso",
                            description: "Membro excluido com sucesso!",
                        });
                        setIsLoading(false);
                        getMembros();
                    })
                        .catch((error) => {
                            notification.error({
                                message: "Erro",
                                description:
                                    "Não foi possivel excluir o membro!",
                            });
                            setIsLoading(false);
                        });

                },
                onCancel() { },
            });
        } catch (error) {
            setIsLoading(false);
            EmitError(error, true, "useEffect");
        }
    }, []);

    const columnsMembros = [
        {
            title: "Foto",
            key: "photo",
            dataIndex: "photo",
            width: "10%",
            render: (photo: any) => {
                return (
                    <img
                        src={
                            photo !== " "
                                ? urlImgMembros + "/" + photo
                                : "/default-avatar.png"
                        }
                        alt="Foto"
                        onClick={() => {
                            setImagemModal(urlImgMembros + "/" + photo);
                            setIsModalImagemOpen(true);
                        }}
                        style={{
                            width: 50,
                            height: 50,
                            borderRadius: 5,
                            cursor: "pointer",
                        }}
                    />
                );
            },
        },
        {
            title: "Nome",
            dataIndex: "name",
            key: "name",
            width: "20%",
            sorter: (a: any, b: any) => a.name.localeCompare(b.name),
            ...getColumnSearchProps("name"),
            showSorterTooltip: false,
        },
        {
            title: "Cargo",
            dataIndex: "responsability",
            key: "responsability",
            width: "20%",
            sorter: (a: any, b: any) => a.responsability.localeCompare(b.responsability),
            ...getColumnSearchProps("responsability"),
            showSorterTooltip: false,
        },
        {
            title: "Mandato",
            dataIndex: "tenure",
            key: "tenure",
            width: "20%",
            sorter: (a: any, b: any) => a.tenure.localeCompare(b.tenure),
            ...getColumnSearchProps("tenure"),
            showSorterTooltip: false,
        },
        {
            title: "Ordem",
            dataIndex: "order",
            key: "order",
            width: "10%",
            sorter: (a: any, b: any) => a.order - b.order,
            showSorterTooltip: false,
            render: (val: any) => {
                return (
                    <span>
                        <Tag color={"green"} key={val}>
                            {val}
                        </Tag>
                    </span>
                );
            },
        },
        {
            title: "Criado em",
            dataIndex: "created_at",
            key: "created_at",
            width: "10%",
            showSorterTooltip: false,
            sorter: (a: any, b: any) =>
                a.created_at.localeCompare(b.created_at),
            render: (criado: any) => moment(criado).format("DD/MM/YYYY HH:mm"),
        },
        {
            title: "Ações",
            dataIndex: "id",
            key: "id",
            width: "10%",
            render: (id: any, record: any) => (
                <span>
                    <Button
                        style={{
                            borderRadius: 5,
                            margin: 1,
                        }}
                        type="primary"
                        size="small"
                        onClick={() => {
                            setIsEditarMembro(true);

                            // Busca em noticias a noticia que vai ser editada
                            let membroFind = membros.find((membro: any) => {
                                return membro.id == id;
                            });


                            let membroOrdens = membros.map((membro: any) => {
                                return { order: membro.order, id: membro.id };
                            });
                            setOrdensMembros(membroOrdens);
                            formMembro.setFieldsValue({
                                membroOrdens: membroOrdens,
                            });
                            setIsModalMembroOpen(true);

                            // Carrega os dados da noticia com block
                            formMembro.setFieldsValue({
                                idMembro: membroFind?.id,
                                nomeMembro: membroFind?.name,
                                cargoMembro: membroFind?.responsability,
                                mandatoMembro: membroFind?.tenure,
                                fotoMembro: membroFind?.photo,
                                ordemMembro: membroFind?.order,
                                descricaoMembro: membroFind?.description,
                            });

                            setFotoMembro(membroFind?.photo);
                            setMembroEditar(membroFind?.id);
                            setIsModalMembroOpen(true);
                        }}
                    >
                        <FaEdit />
                    </Button>
                    <Divider type="vertical" />
                    <Button
                        style={{
                            borderRadius: 5,
                            margin: 1,
                        }}
                        type="primary"
                        danger
                        size="small"
                        onClick={() => {
                            excluirMembro(id);
                        }}
                    >
                        <FaTrash />
                    </Button>
                </span>
            ),
        },
    ];

    useEffect((): void => {
        // setIsSalvarOrEditar(true);

        const buscaDadosIniciais = async () => {
            try {
                refController.current = new AbortController();

                if (userCan('manage_member')) {
                    //Se tiver id
                    if (dataEntity.entity.id) {
                        getMembros();
                    }
                }

            } catch (error) {
                setIsLoading(false);
            }
        };

        buscaDadosIniciais();
    }, []);

    const getMembros = useCallback(() => {
        setIsLoading(true);
        const buscaDeDados = async () => {
            try {

                const data = await MembrosService.getGestorMembro(
                    dataEntity.entity.id,
                    refController.current.signal
                );

                data.sort((a: any, b: any) => {
                    return (
                        moment(b.created_at).unix() -
                        moment(a.created_at).unix()
                    );
                });
                setMembros(data);

                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                EmitError(error, true, "useEffect");
            }
        };
        return buscaDeDados();
    }, []);

    const salvarMembro = useCallback(async (values: any) => {
        const ordensMembros = formMembro.getFieldValue("membroOrdens");

        // Converter values.ordemMembro para o mesmo tipo que ordem.ordem para uma comparação mais precisa
        const ordemMembro = parseInt(values.ordemMembro);
        if (ordensMembros !== undefined) {

            // Busca se existe ordem igual com id diferente
            let ordemEncontrada = ordensMembros.find((ordem: any) => {
                // Converter ordem.ordem para o mesmo tipo que values.ordemMembro
                const ordemNoArray = parseInt(ordem.order);
                return (
                    ordemNoArray === ordemMembro && ordem.id !== values.idMembro
                );
            });
            if (ordemEncontrada) {
                notification.error({
                    message: "Erro",
                    description: "Ordem já cadastrada em outro membro!",
                });
                return;
            }
        }

        try {
            setIsLoading(true);
            const data = new FormData();
            data.append("name", values.nomeMembro);
            data.append(
                "description",
                values.descricaoMembro ? values.descricaoMembro : " "
            );
            data.append(
                "entityId", values.entityId
            );
            data.append("tenure", values.mandatoMembro);
            data.append("responsability", values.cargoMembro);
            data.append("order", values.ordemMembro);
            const foto = formMembro.getFieldValue("fotoMembro");
            data.append("photo", foto);


            if (values.idMembro) {

                MembrosService.putGestorMembro(
                    values.idMembro,
                    data,
                    refController.current.signal
                ).then((result) => {
                    notification.success({
                        message: "Sucesso",
                        description: "Membro atualizado com sucesso!",
                    });
                    getMembros();
                    setIsModalMembroOpen(false);
                    setIsLoading(false);
                })
                    .catch((error) => {
                        setIsLoading(false);
                        EmitError(error, true, "useEffect");
                    });

            } else {

                MembrosService.postGestorMembro(
                    data,
                    refController.current.signal
                ).then((result) => {
                    notification.success({
                        message: "Sucesso",
                        description: "Membro salvo com sucesso!",
                    });
                    getMembros();
                    setIsModalMembroOpen(false);
                    setIsLoading(false);
                })
                    .catch((error) => {
                        setIsLoading(false);
                        EmitError(error, true, "useEffect");
                    });

            }
        } catch (error) {
            let msgErro: any = error as Error;
            notification.error({
                message: "Erro",
                description: msgErro?.response?.data?.message
                    ? msgErro?.response?.data?.message
                    : "Entre em contato com o suporte!",
            });
            setIsLoading(false);
            return false;
        }
    }, []);

    return {
        dataEntity,
        userCan,
        setIsEditarMembro,
        formMembro,
        setFotoMembro,
        showModalMembro,
        columnsMembros,
        membros,
        theme,
        refContagemDoFiltroMembros,
        isLoading,
        isModalMembroOpen,
        handleMembroCancel,
        refFormMembro,
        beforeUploadMembro,
        onClickBotaoSalvarMembro,
        isEditarMembro,
        fotoMembro,
        isModalImagemOpen,
        setIsModalImagemOpen,
        imagemModal,
    }
}
